import { Component, MouseEventHandler } from 'react'
import * as ReactDOM from 'react-dom'
import * as styles from 'components/features/pfm/Overlay/Overlay.css'
import * as cn from 'classnames'
import { captureWarning } from 'helpers/errorHelpers'
import { isShrunkSidebarUrl } from 'helpers/pageHelpers'
import { isArrowKey, KEY_CODES } from 'helpers/keyboardHelpers'
import { POPUP_PORTAL_ID } from 'constants/component'
import { RouteComponentProps } from 'react-router-dom'
import { convertVhToPx } from 'helpers/styleHelpers'
import { IS_MOBILE } from 'helpers/userAgentHelpers'
import { QAMarked } from 'interfaces/component'
import { PreventScroll } from 'components/features/pfm/PreventScroll/PreventScroll'

export type OverlayStateProps = {
  isAuthenticated: boolean
}

export type OverlayDispatchProps = {}

export type OverlayOwnProps =
  & QAMarked
  & {
    isFullScreen?: boolean
    onClose?: () => void
  }

type OverlayProps =
  & OverlayStateProps
  & OverlayDispatchProps
  & OverlayOwnProps
  & RouteComponentProps<{}>


type OverlayState = {
  height: CSSStyleDeclaration['height']
}

export class Overlay extends Component<OverlayProps, OverlayState> {
  private static portalRootNode: HTMLElement | null = document.getElementById(POPUP_PORTAL_ID)

  state = {
    height: '',
  }

  private overlayNode: HTMLDivElement | null = null
  private isOuterEventDisabled = false

  render () {
    const {
      location,
      children,
      qaMarker,
      isAuthenticated,
    } = this.props

    const { height } = this.state

    if (!Overlay.portalRootNode) {
      captureWarning('No `portalRootNode` exist', {
        target: 'ModalComponent',
      })
      return null
    }

    const overlayClassName = cn(styles.overlay, {
      [styles.overlayShrunkSidebar]: isShrunkSidebarUrl(location.pathname, isAuthenticated),
      [styles.isHeightSet]: Boolean(height),
      // [styles.fixSafariIos]: IS_IOS,
    })
    const style = height ? { height } : undefined

    return ReactDOM.createPortal(
      <PreventScroll
        data-qa-marker={ qaMarker }
        className={ overlayClassName }
        innerRef={ (el: HTMLDivElement) => this.overlayNode = el }
        onClick={ this.handleOverlayClick }
        style={ style }
      >
        { children }
      </PreventScroll>,
      Overlay.portalRootNode,
    )
  }

  componentDidMount () {
    window.addEventListener('wheel', this.preventDefault, { passive: false })
    window.addEventListener('keydown', this.preventDefaultForScrollKeys)

    if (!this.props.isFullScreen) {
      window.addEventListener('touchmove', this.preventOuterEventDefault, { passive: false })
      this.isOuterEventDisabled = true
    }

    if (IS_MOBILE) {
      this.setHeight()
      window.addEventListener('resize', this.setHeight)
    }
  }

  componentWillUnmount () {
    window.removeEventListener('wheel', this.preventDefault)
    window.removeEventListener('keydown', this.preventDefaultForScrollKeys)

    if (this.isOuterEventDisabled) {
      window.removeEventListener('touchmove', this.preventOuterEventDefault, { passive: false } as any)
    }

    if (IS_MOBILE) {
      window.removeEventListener('resize', this.setHeight)
    }
  }

  private preventDefault (e: UIEvent) {
    e.preventDefault()
  }

  private preventOuterEventDefault: EventListener = (e) => {
    if (!this.overlayNode) {
      return
    }

    const shouldPrevent = e.target === this.overlayNode || !this.overlayNode.contains(e.target as Node)
    if (shouldPrevent) {
      e.preventDefault()
    }
  }

  private preventDefaultForScrollKeys = (e: KeyboardEvent) => {
    const { onClose } = this.props

    if (e.keyCode === KEY_CODES.ESC && onClose) { // esc
      onClose()
      return
    }

    if (isArrowKey(e.keyCode)) {
      this.preventOuterEventDefault(e)
    }
  }

  private setHeight = () => {
    if (!this.overlayNode) {
      return
    }

    this.setState({
      height: convertVhToPx(styles.overlayMobileHeight),
    })
  }

  private handleOverlayClick: MouseEventHandler<HTMLDivElement> = (e) => {
    const { onClose } = this.props
    if (onClose && this.overlayNode && e.target === this.overlayNode) {
      onClose()
    }
  }
}
